import { Button } from "react-bootstrap";
import React, { useState, useEffect } from "react";
import { api_url } from "../../constant/constant";
import { useTranslation } from "react-i18next";
import { showToast } from "../../Components/ShowToast/ToastNotification";

const Customer = () => {
  const { t, i18n } = useTranslation();
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const [formData, setFormData] = useState({
    host_name: "",
    customer_name: "",
    dashboard_msg_en: "",
    dashboard_msg_ar: "",
    session_lifetime: 0,
    ldap_enabled: 0,
    sso_enabled: 0,
    login_using_email_enabled: 0,
    language_id: 0,
  });

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setLogoFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const fetchLogo = async () => {
    try {
      const token = localStorage.getItem("userToken");
      const response = await fetch(`${api_url}/customer/logo`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok && data.status === "success") {
        setLogoUrl(data.data.logo_url);
      } else {
        showToast(t("failedtofetchlogo"), "error");
      }
    } catch (err) {
      showToast(t("failedtofetchlogo"), "error");
    }
  };

  const fetchCustomerData = async () => {
    try {
      const token = localStorage.getItem("userToken");
      const response = await fetch(`${api_url}/customer/settings`, {
        mode: "cors",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        showToast(t("failedtofetchdata"), "error");
        return;
      }

      const data = await response.json();

      if (data.status === "success") {
        setFormData({
          host_name: data.data.host_name ?? "",
          customer_name: data.data.customer_name ?? "",
          dashboard_msg_en: data.data.dashboard_msg_en ?? "",
          dashboard_msg_ar: data.data.dashboard_msg_ar ?? "",
          session_lifetime: data.data.session_lifetime ?? 0,
          ldap_enabled: data.data.ldap_enabled ?? 0,
          sso_enabled: data.data.sso_enabled ?? 0,
          login_using_email_enabled: data.data.login_using_email_enabled ?? 0,
          language_id: data.data.language_id ?? 0,
        });
      }
    } catch (err) {
      showToast(t("errfetchingdata"), "error");
    }
  };

  useEffect(() => {
    fetchCustomerData();
    fetchLogo();
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleLogoUpload = async () => {
    if (!logoFile) {
      showToast(t("pleaseselectlogo"), "warning");
      return;
    }

    try {
      const token = localStorage.getItem("userToken");
      const uploadData = new FormData();
      uploadData.append("logo", logoFile);

      const response = await fetch(`${api_url}/customer/logo`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: uploadData,
      });

      const data = await response.json();

      if (response.ok && data.status === "success") {
        showToast(t("logouploadedsuccessfully"), "success");

        setLogoFile(null);

        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);

        await fetchLogo();
      } else {
        showToast(t("failedtouploadlogo"), "error");
      }
    } catch (err) {
      showToast(t("erroruploadinglogo"), "error");
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;

    if (type === "checkbox") {
      const newValue = checked ? 1 : 0;

      setFormData((prev) => {
        const updated = {
          ldap_enabled: 0,
          sso_enabled: 0,
          login_using_email_enabled: 0,
        };

        updated[name] = newValue;

        return {
          ...prev,
          ...updated,
        };
      });
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: name === "language_id" ? Number(value) : value,
    }));
  };

  const updateCustomerData = async () => {
    try {
      const token = localStorage.getItem("userToken");
      const response = await fetch(`${api_url}/customer/settings/update`, {
        mode: "cors",
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        showToast(t("failedtoupdatedata"), "error");
        return;
      }

      const data = await response.json();

      if (data.status === "success") {
        await fetchCustomerData();
        showToast(t("customerdataupdatedsuccessfully"), "success");

        if (formData.language_id === 1) {
          i18n.changeLanguage("en");
        } else if (formData.language_id === 2) {
          i18n.changeLanguage("ar");
        }
      } else {
        showToast(t("updatefailed"), "error");
      }
    } catch (err) {
      showToast(t("errorupdatingdata"), "error");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateCustomerData();
  };

  return (
    <div
      className="py-4"
      style={{
        border: "1px solid #EAEAEA",
        borderRadius: "10px",
        padding: "0px 15px",
      }}
    >
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">{t("HostName")}</label>
              <input
                type="text"
                name="host_name"
                className="form-control"
                onChange={handleChange}
                value={formData.host_name}
              />
            </div>
          </div>

          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">{t("CustomerName")}</label>
              <input
                type="text"
                name="customer_name"
                className="form-control"
                onChange={handleChange}
                value={formData.customer_name}
                disabled
              />
            </div>
          </div>

          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                {t("DashboardMessage(English)")}
              </label>
              <textarea
                name="dashboard_msg_en"
                className="form-control"
                onChange={handleChange}
                value={formData.dashboard_msg_en}
                rows="2"
              />
            </div>
          </div>

          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">
                {t("DashboardMessage(Arabic)")}
              </label>
              <textarea
                name="dashboard_msg_ar"
                className="form-control"
                onChange={handleChange}
                value={formData.dashboard_msg_ar}
                rows="2"
              />
            </div>
          </div>

          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">{t("SessionLifetime")}</label>
              <input
                type="number"
                name="session_lifetime"
                className="form-control"
                onChange={handleChange}
                value={formData.session_lifetime}
              />
            </div>
          </div>

          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">{t("UploadLogo")}</label>

              <div className="d-flex align-items-center gap-2">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="form-control"
                  onChange={handleFileChange}
                />

                <Button
                  variant="secondary"
                  type="button"
                  onClick={handleLogoUpload}
                >
                  {t("Upload")}
                </Button>
              </div>

              <small className="text-muted d-block mt-1">
                {t("Allowedformats")}: JPG, JPEG, PNG, WEBP
              </small>

              {previewUrl ? (
                <div className="mt-3">
                  <img
                    src={previewUrl}
                    alt="Selected Logo Preview"
                    style={{
                      width: "120px",
                      height: "auto",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                    }}
                  />
                  <p className="text-muted mt-1">{t("SelectedLogoPreview")}</p>
                </div>
              ) : logoUrl ? (
                <div className="mt-3">
                  <img
                    src={logoUrl}
                    alt="Current Logo"
                    style={{
                      width: "120px",
                      height: "auto",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                    }}
                  />
                  <p className="text-muted mt-1">{t("CurrentLogo")}</p>
                </div>
              ) : null}
            </div>
          </div>

          <div className="p-4 mb-4">
            <div
              className="row"
              style={{
                border: "1px solid #EAEAEA",
                borderRadius: "10px",
                padding: "0px 15px",
              }}
            >
              <div className="col-md-6 mt-4">
                <div className="mb-4">
                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="ldap_enabled"
                      checked={formData.ldap_enabled === 1}
                      onChange={handleChange}
                      id="ldapEnabled"
                    />
                    <label className="form-check-label" htmlFor="ldapEnabled">
                      LDAP
                    </label>
                  </div>

                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="sso_enabled"
                      checked={formData.sso_enabled === 1}
                      onChange={handleChange}
                      id="ssoEnabled"
                    />
                    <label className="form-check-label" htmlFor="ssoEnabled">
                      SSO
                    </label>
                  </div>

                  <div className="form-check mb-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name="login_using_email_enabled"
                      checked={formData.login_using_email_enabled === 1}
                      onChange={handleChange}
                      id="loginUsingEmailEnabled"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="loginUsingEmailEnabled"
                    >
                      {t("LoginUsingEmail")}
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-md-6 mt-4">
                <div className="mb-1">
                  <label className="form-label">{t("LanguageSelection")}</label>

                  <div className="form-check mb-3">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="language_id"
                      value="1"
                      checked={formData.language_id === 1}
                      onChange={handleChange}
                      id="englishLanguage"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="englishLanguage"
                    >
                      {t("English")}
                    </label>
                  </div>

                  <div className="form-check mb-0">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="language_id"
                      value="2"
                      checked={formData.language_id === 2}
                      onChange={handleChange}
                      id="arabicLanguage"
                    />
                    <label
                      className="form-check-label"
                      htmlFor="arabicLanguage"
                    >
                      {t("Arabic")}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Button className="mt-2" variant="primary" type="submit">
          {t("UpdateCustomerSettings")}
        </Button>
      </form>
    </div>
  );
};

export default Customer;